import { Controller } from "@hotwired/stimulus"

export default class extends Controller {

  connect() {
    this.handleFormChange = this.updateDriveTestPreviousInstructorValidation.bind(this);
    this.observeInstructorAndLocationForm();
    document.addEventListener("change", this.handleFormChange);
    this.toggleErrorPanel(false);
    this.updateDriveTestPreviousInstructorValidation();
  }

  disconnect() {
    document.removeEventListener("change", this.handleFormChange);
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  toggleWarningsPanel(show, warningMessage = "") {
    const warningSection = document.getElementById("warnings-section")
    const warningList = document.getElementById("warnings-list")

    if (show) {
      warningSection.classList.remove("u-hidden");
      warningList.textContent = warningMessage;
    } else {
      warningSection.classList.add("u-hidden");
      warningList.textContent = "";
    }
  }

  toggleErrorPanel(show, errorMessage = "") {
    const errorSection = document.getElementById("error-section");
    const errorList = document.getElementById("errors-list");

    if (!errorSection || !errorList) return;

    if (show) {
      errorSection.classList.remove("u-hidden");
      errorList.textContent = errorMessage;
    } else {
      errorSection.classList.add("u-hidden");
      errorList.textContent = "";
    }
  }

  currentLessonType() {
    return document.getElementById("lesson_type")?.value || "";
  }

  saveButton() {
    return document.getElementById("quick-look-add-lesson-save-button");
  }

  nestedEditorOpen() {
    return Boolean(
      document.getElementById("quicklook-schedule-location-edit") ||
      document.getElementById("quicklook-schedule-instructor-edit")
    );
  }

  shouldEnforceDriveTestPreviousInstructorRule() {
    return document.getElementById("enforce_previous_test_instructor_rule")?.value === "true";
  }

  observeInstructorAndLocationForm() {
    const instructorLocationContainer = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location_form"
    );

    if (!instructorLocationContainer) return;

    this.observer = new MutationObserver(() => {
      this.updateDriveTestPreviousInstructorValidation();
    });

    this.observer.observe(instructorLocationContainer, {
      childList: true,
      subtree: true,
    });
  }

  selectedPupilPreviousDriveTestInstructorIds() {
    const selectedPupil = document.querySelector('input[name="selected-item-pupil"]:checked');
    if (!selectedPupil) return [];

    try {
      const parsed = JSON.parse(selectedPupil.dataset.driveTestPreviousInstructorIds || "[]");
      return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
    } catch (_error) {
      return [];
    }
  }

  driveTestPreviousInstructorMessage() {
    if (!this.shouldEnforceDriveTestPreviousInstructorRule()) return "";
    if (this.currentLessonType() !== "DriveTest") return "";

    const instructorId = document.getElementById("instructor_id")?.value || "";
    if (!instructorId) return "";

    const previousInstructorIds = this.selectedPupilPreviousDriveTestInstructorIds();
    if (previousInstructorIds.includes(String(instructorId))) {
      return "Warning: This student has had lessons with this instructor previously.";
    }

    return "";
  }

  driveTestPretestDurationInMinutes() {
    return Number(
      document.getElementById("new_lesson_specific_duration_in_minutes")?.value || 0
    );
  }

  driveTestPretestAssignmentMessage() {
    if (this.currentLessonType() !== "DriveTest") return "";

    const pretestDurationInMinutes = this.driveTestPretestDurationInMinutes();
    if (pretestDurationInMinutes <= 0) return "";

    const pretestInstructorId =
      document.getElementById("pretest_instructor_id")?.value || "";
    const pretestVehicleId =
      document.getElementById("pretest_vehicle_id")?.value || "";

    if (!pretestInstructorId && !pretestVehicleId) {
      return "Please select a pre-test instructor and vehicle.";
    }

    if (!pretestInstructorId) {
      return "Please select a pre-test instructor.";
    }

    if (!pretestVehicleId) {
      return "Please select a pre-test vehicle.";
    }

    if (!this.shouldEnforceDriveTestPreviousInstructorRule()) return "";

    const testInstructorId = document.getElementById("instructor_id")?.value || "";
    if (
      testInstructorId &&
      String(pretestInstructorId) === String(testInstructorId)
    ) {
      return "Pre-test and drive test instructors must be different when the Student cannot test with a previous lesson instructor setting is enabled.";
    }

    return "";
  }

  updateDriveTestPreviousInstructorValidation() {
    const errorMessage =
      this.driveTestPretestAssignmentMessage()
    const warningMessage = 
      this.driveTestPreviousInstructorMessage();
    const saveButton = this.saveButton();
    const nestedEditorOpen = this.nestedEditorOpen();

    if (errorMessage.length > 0) {
      this.toggleErrorPanel(true, errorMessage);
    } else {
      this.toggleErrorPanel(false);
    }

    if (warningMessage.length > 0) {
      this.toggleWarningsPanel(true, warningMessage)
    } else {
      this.toggleWarningsPanel(false);
    }

    if (saveButton) {
      const isInvalid = errorMessage.length > 0 || nestedEditorOpen;
      saveButton.disabled = isInvalid;
      saveButton.setAttribute("aria-disabled", isInvalid ? "true" : "false");
      saveButton.classList.toggle("is-disabled", isInvalid);
    }
  }

  async validate(event) {
    this.updateDriveTestPreviousInstructorValidation();

    const driving_school_note_pupil_id = document.getElementById("driving_school_note_pupil_id").value;
    
    if (isNaN(driving_school_note_pupil_id)) {
      alert("Please select a pupil");
      event.preventDefault();
      return;
    }

    const selected_item_pupil_id = document.getElementById("selected-item-pupil-" + driving_school_note_pupil_id).value;
    // check the selected_item_pupil_id value is the ID
    if (selected_item_pupil_id != driving_school_note_pupil_id) {
      alert("Please select a pupil");
      event.preventDefault();
      return;
    }

    // find instructor_id and vehicle_id and make sure they are not blank
    const instructor_id = document.getElementById("instructor_id");
    
    if (instructor_id == undefined || instructor_id.value == "" || isNaN(instructor_id.value)) {
      alert("Please check the student and instructor are selected");
      event.preventDefault();
      return;
    }

    const vehicle_id = document.getElementById("vehicle_id");
    if (vehicle_id == undefined || vehicle_id.value == "" || isNaN(vehicle_id.value)) {
      alert("Please check the student and vehicle are selected");
      event.preventDefault();
      return;
    }

    // same with start_date and start_time
    const start_date = document.getElementById("start_date");
    if (start_date == undefined || start_date.value == "") {
      alert("Please select a start date");
      event.preventDefault();
      return;
    }

    const start_time = document.getElementById("start_time");
    if (start_time == undefined || start_time.value == "") {
      alert("Please select a start time");
      event.preventDefault();
      return;
    }
  }
}
